import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, ApiFail } from "../api/client";
import type { AuthResponse, MfaRequiredResponse } from "../api/types";
import { useSession } from "../session";
import { AuthShell } from "../components/AuthShell";
import { Input } from "@grackle/ui";
import { FormNotice, LoadingButton } from "../components/form";
import { OAuthButtons } from "../components/OAuthButtons";
import { ssoErrorMessage, ssoRequiredSlugFromParams, ssoStartUrl } from "../lib/sso";
import { DESKTOP_AUTH_RETURN_KEY } from "../components/DesktopAuthReturn";
import {
  EVT_AUTH_CHANGED,
  EVT_AUTH_FAILED,
  invoke,
  isDesktop,
  isMobileShell,
  listenDesktop,
  listenDesktopPayload,
  loadDesktopInfo,
  peekDesktopInfo,
} from "../desktop";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/; // §2.10

const OAUTH_ERROR_MESSAGES: Record<string, string> = {
  access_denied: "Sign-in was cancelled.",
};

function isMfaRequired(res: AuthResponse | MfaRequiredResponse): res is MfaRequiredResponse {
  return (res as MfaRequiredResponse).mfa_required === true;
}

/** Only ever navigate within this app -- a `next` sourced from a query string is attacker
 * input (e.g. a crafted sign-in link) and `//evil.example` parses as a protocol-relative URL a
 * browser would happily follow. */
function safeNext(next: string | null): string | null {
  if (!next) return null;
  if (!next.startsWith("/") || next.startsWith("//")) return null;
  return next;
}

export default function SignIn() {
  const navigate = useNavigate();
  const { setUser, refresh } = useSession();
  const [searchParams] = useSearchParams();
  // F13c-1 / SD42: oauth::callback redirects here with sso_error=sso_required&org_slug=… when
  // "continue with Google/Microsoft" hits a verified, SSO-enforced domain; go straight to the
  // Continue-with-SSO panel instead of a dead-end error.
  const redirectSsoSlug = ssoRequiredSlugFromParams(searchParams);
  const ssoError = redirectSsoSlug ? null : searchParams.get("sso_error");
  const oauthError = searchParams.get("oauth_error");
  const next = safeNext(searchParams.get("next"));
  const destination = next ?? "/orgs";

  // FR-A4: the packaged desktop and Android/iOS shells (SD33) route sign-in through the system
  // browser instead of an embedded password form once they know their API origin (every
  // packaged build has one; `tauri dev`'s empty origin keeps today's same-origin cookie flow and
  // never sets this). Seeded from the cache `main.tsx` fills before the first render, so the
  // password form — which can only POST to a relative `/api/v1` on tauri://localhost and fail —
  // is never on screen in a packaged shell, not even for the first frame.
  const [desktopApiOrigin, setDesktopApiOrigin] = useState<string | null>(
    () => peekDesktopInfo()?.api_origin || null,
  );
  const [desktopWaiting, setDesktopWaiting] = useState(false);
  const [desktopError, setDesktopError] = useState<string | undefined>();

  useEffect(() => {
    if (!isDesktop()) return;
    let cancelled = false;
    loadDesktopInfo()
      .then((info) => {
        if (!cancelled && info?.api_origin) setDesktopApiOrigin(info.api_origin);
      })
      .catch((err) => console.error("desktop_info failed", err));
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!desktopApiOrigin) return;
    const unlisteners: (() => void)[] = [];
    let cancelled = false;
    const keep = (fn: () => void) => {
      if (cancelled) fn();
      else unlisteners.push(fn);
    };
    void listenDesktop(EVT_AUTH_CHANGED, () => {
      setDesktopWaiting(false);
      void refresh().then(() => {
        if (!cancelled) navigate(destination, { replace: true });
      });
    }).then(keep);
    void listenDesktopPayload<string>(EVT_AUTH_FAILED, (message) => {
      setDesktopWaiting(false);
      setDesktopError(message || "Sign-in didn't complete. Try again.");
    }).then(keep);
    return () => {
      cancelled = true;
      unlisteners.forEach((fn) => fn());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [desktopApiOrigin]);

  async function beginDesktopSignIn() {
    setDesktopError(undefined);
    setDesktopWaiting(true);
    try {
      await invoke("auth_begin_sign_in");
    } catch {
      setDesktopWaiting(false);
      setDesktopError("Couldn't open your browser. Try again.");
    }
  }

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailError, setEmailError] = useState<string | undefined>();
  const [passwordError, setPasswordError] = useState<string | undefined>();
  const [formError, setFormError] = useState<string | undefined>();
  const [ssoOrgSlug, setSsoOrgSlug] = useState<string | null>(redirectSsoSlug);
  const [submitting, setSubmitting] = useState(false);

  // FR-A6: signin returned {mfa_required: true, mfa_token} instead of a session — hold onto
  // the token and swap the form for a second-factor prompt.
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState("");
  const [mfaError, setMfaError] = useState<string | undefined>();
  const [mfaSubmitting, setMfaSubmitting] = useState(false);

  function validate(): boolean {
    let ok = true;
    if (!EMAIL_RE.test(email.trim())) {
      setEmailError("Enter a valid email address");
      ok = false;
    } else {
      setEmailError(undefined);
    }
    if (password.length === 0) {
      setPasswordError("Enter your password");
      ok = false;
    } else {
      setPasswordError(undefined);
    }
    return ok;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setFormError(undefined);
    setSsoOrgSlug(null);
    if (!validate()) return;

    setSubmitting(true);
    try {
      const res = await api<AuthResponse | MfaRequiredResponse>("POST", "/auth/signin", {
        email: email.trim().toLowerCase(),
        password,
      });
      if (isMfaRequired(res)) {
        setMfaToken(res.mfa_token);
        return;
      }
      setUser(res.user);
      await refresh();
      if (next) sessionStorage.removeItem(DESKTOP_AUTH_RETURN_KEY);
      navigate(destination, { replace: true });
    } catch (err) {
      if (err instanceof ApiFail) {
        if (err.code === "invalid_credentials") {
          setPasswordError("Incorrect email or password");
        } else if (err.code === "rate_limited") {
          setFormError("Too many attempts. Wait a moment and try again.");
        } else if (err.code === "account_locked") {
          const lockedUntil = err.data?.locked_until;
          const when = typeof lockedUntil === "string" ? new Date(lockedUntil) : null;
          setFormError(
            when
              ? `Too many failed attempts. Try again after ${when.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}.`
              : "Too many failed attempts. Try again later.",
          );
        } else if (err.code === "sso_required") {
          const slug = err.data?.org_slug;
          setSsoOrgSlug(typeof slug === "string" ? slug : null);
        } else {
          setFormError(err.message);
        }
      } else {
        setFormError("Couldn't reach Grackle. Check your connection and try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function onMfaSubmit(e: FormEvent) {
    e.preventDefault();
    if (!mfaToken) return;
    setMfaError(undefined);
    if (!mfaCode.trim()) {
      setMfaError("Enter your 6-digit code or a recovery code");
      return;
    }
    setMfaSubmitting(true);
    try {
      const res = await api<AuthResponse>("POST", "/auth/mfa/verify", {
        mfa_token: mfaToken,
        code: mfaCode.trim(),
      });
      setUser(res.user);
      await refresh();
      if (next) sessionStorage.removeItem(DESKTOP_AUTH_RETURN_KEY);
      navigate(destination, { replace: true });
    } catch (err) {
      if (err instanceof ApiFail) {
        setMfaError(err.code === "invalid_credentials" ? "That code didn't work." : err.message);
      } else {
        setMfaError("Couldn't reach Grackle. Check your connection and try again.");
      }
    } finally {
      setMfaSubmitting(false);
    }
  }

  // FR-A4: a packaged build with a resolved API origin never shows the embedded password
  // form -- cookie auth cannot cross tauri://localhost / tauri.localhost into the API origin
  // (no shared cookie jar, and the server's CORS layer deliberately never grants
  // allow-credentials). `tauri dev`'s empty origin falls through to the normal form below,
  // same as the browser.
  if (desktopApiOrigin) {
    return (
      <AuthShell title="Sign in to Grackle" subtitle={isMobileShell() ? "Grackle app" : "Grackle desktop"}>
        {desktopError ? <FormNotice tone="error">{desktopError}</FormNotice> : null}
        <div className="auth-form">
          {desktopWaiting ? (
            <>
              <FormNotice>Finish signing in in your browser…</FormNotice>
              <LoadingButton type="button" variant="ghost" block onClick={() => setDesktopWaiting(false)}>
                Cancel
              </LoadingButton>
              <LoadingButton type="button" block onClick={() => void beginDesktopSignIn()}>
                Open browser again
              </LoadingButton>
            </>
          ) : (
            <LoadingButton type="button" block onClick={() => void beginDesktopSignIn()}>
              Sign in with your browser
            </LoadingButton>
          )}
        </div>
      </AuthShell>
    );
  }

  if (mfaToken) {
    return (
      <AuthShell
        title="Two-factor verification"
        subtitle="Enter the 6-digit code from your authenticator app, or one of your recovery codes."
      >
        <form className="auth-form" onSubmit={onMfaSubmit} noValidate>
          <Input
            label="Verification code"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="123456"
            value={mfaCode}
            onChange={(e) => setMfaCode(e.target.value)}
            error={mfaError}
            autoFocus
          />
          <LoadingButton type="submit" block loading={mfaSubmitting}>
            {mfaSubmitting ? "Verifying…" : "Verify"}
          </LoadingButton>
          <LoadingButton type="button" variant="ghost" block onClick={() => { setMfaToken(null); setMfaCode(""); setMfaError(undefined); }}>
            Back to sign in
          </LoadingButton>
        </form>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Sign in"
      subtitle="Use your email and password to continue to Grackle."
      footer={
        <>
          No account yet? <Link to="/signup">Create one</Link>.
        </>
      }
    >
      {ssoError ? (
        <FormNotice tone="error">{ssoErrorMessage(ssoError)}</FormNotice>
      ) : null}
      {oauthError ? (
        <FormNotice tone="error">{OAUTH_ERROR_MESSAGES[oauthError] ?? "Sign-in didn't complete."}</FormNotice>
      ) : null}

      {ssoOrgSlug ? (
        <div className="auth-form">
          <FormNotice>
            This account signs in with your organization's single sign-on — password login isn't
            available for it.
          </FormNotice>
          <LoadingButton block onClick={() => { window.location.href = ssoStartUrl(ssoOrgSlug); }}>
            Continue with SSO
          </LoadingButton>
          <LoadingButton type="button" variant="ghost" block onClick={() => setSsoOrgSlug(null)}>
            Use a different email
          </LoadingButton>
        </div>
      ) : (
        <>
          <form className="auth-form" onSubmit={onSubmit} noValidate>
            <Input
              label="Email"
              type="email"
              autoComplete="email"
              placeholder="you@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={emailError}
            />
            <div>
              <Input
                label="Password"
                type="password"
                autoComplete="current-password"
                placeholder="••••••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                error={passwordError}
              />
              <div className="auth-forgot">
                <Link to="/forgot-password">Forgot password?</Link>
              </div>
            </div>
            {formError ? <FormNotice tone="error">{formError}</FormNotice> : null}
            <LoadingButton type="submit" block loading={submitting}>
              {submitting ? "Signing in…" : "Sign in"}
            </LoadingButton>
          </form>
          <OAuthButtons label="Or sign in with" />
        </>
      )}
    </AuthShell>
  );
}
